<template>
  <div class="statistics-container">
    <!-- PC端数据统计 -->
    <div class="pc-statistics">
      <div class="sidebar">
        <div class="sidebar-header">
          <div class="logo-icon">✓</div>
          <h1 class="logo-text">TaskFlow</h1>
        </div>
        <div class="sidebar-menu">
          <a-menu :selected-keys="['statistics']" @click="handleMenuClick">
            <a-menu-item key="task-list">
              <a-icon type="appstore" /> 任务管理
            </a-menu-item>
            <a-menu-item key="statistics">
              <a-icon type="bar-chart" /> 数据统计
            </a-menu-item>
            <a-menu-item key="user-management">
              <a-icon type="team" /> 用户管理
            </a-menu-item>
            <a-menu-item key="settings">
              <a-icon type="setting" /> 设置
            </a-menu-item>
          </a-menu>
        </div>
        <div class="sidebar-footer">
          <div class="user-info">
            <a-avatar icon="user" />
            <span class="user-name">{{ user.email }}</span>
          </div>
          <a-button type="text" @click="handleLogout">退出登录</a-button>
        </div>
      </div>
      
      <div class="main-content">
        <div class="main-header">
          <h2 class="page-title">数据统计</h2>
          <div class="date-range">
            <a-date-picker 
              v-model="dateRange" 
              format="YYYY-MM-DD" 
              @change="handleDateChange"
            />
          </div>
        </div>
        
        <div class="stats-overview">
          <div class="stat-card">
            <div class="stat-icon task-icon">
              <a-icon type="appstore" />
            </div>
            <div class="stat-content">
              <h3 class="stat-title">总任务数</h3>
              <p class="stat-value">{{ statistics.totalTasks }}</p>
              <p class="stat-change" :class="{ positive: statistics.totalTasksChange > 0, negative: statistics.totalTasksChange < 0 }">
                {{ statistics.totalTasksChange > 0 ? '+' : '' }}{{ statistics.totalTasksChange }}% 较上周
              </p>
            </div>
          </div>
          
          <div class="stat-card">
            <div class="stat-icon completed-icon">
              <a-icon type="check-circle" />
            </div>
            <div class="stat-content">
              <h3 class="stat-title">已完成任务</h3>
              <p class="stat-value">{{ statistics.completedTasks }}</p>
              <p class="stat-change" :class="{ positive: statistics.completedTasksChange > 0, negative: statistics.completedTasksChange < 0 }">
                {{ statistics.completedTasksChange > 0 ? '+' : '' }}{{ statistics.completedTasksChange }}% 较上周
              </p>
            </div>
          </div>
          
          <div class="stat-card">
            <div class="stat-icon pending-icon">
              <a-icon type="clock-circle" />
            </div>
            <div class="stat-content">
              <h3 class="stat-title">待处理任务</h3>
              <p class="stat-value">{{ statistics.pendingTasks }}</p>
              <p class="stat-change" :class="{ positive: statistics.pendingTasksChange < 0, negative: statistics.pendingTasksChange > 0 }">
                {{ statistics.pendingTasksChange > 0 ? '+' : '' }}{{ statistics.pendingTasksChange }}% 较上周
              </p>
            </div>
          </div>
          
          <div class="stat-card">
            <div class="stat-icon overdue-icon">
              <a-icon type="exclamation-circle" />
            </div>
            <div class="stat-content">
              <h3 class="stat-title">逾期任务</h3>
              <p class="stat-value">{{ statistics.overdueTasks }}</p>
              <p class="stat-change" :class="{ positive: statistics.overdueTasksChange < 0, negative: statistics.overdueTasksChange > 0 }">
                {{ statistics.overdueTasksChange > 0 ? '+' : '' }}{{ statistics.overdueTasksChange }}% 较上周
              </p>
            </div>
          </div>
        </div>
        
        <div class="charts-section">
          <div class="chart-card">
            <h3 class="chart-title">任务完成趋势</h3>
            <div class="chart-content">
              <div class="trend-chart">
                <!-- 模拟趋势图 -->
                <div class="trend-item">
                  <div class="trend-bar" style="height: 30%;"></div>
                  <span class="trend-label">周一</span>
                </div>
                <div class="trend-item">
                  <div class="trend-bar" style="height: 45%;"></div>
                  <span class="trend-label">周二</span>
                </div>
                <div class="trend-item">
                  <div class="trend-bar" style="height: 60%;"></div>
                  <span class="trend-label">周三</span>
                </div>
                <div class="trend-item">
                  <div class="trend-bar" style="height: 40%;"></div>
                  <span class="trend-label">周四</span>
                </div>
                <div class="trend-item">
                  <div class="trend-bar" style="height: 70%;"></div>
                  <span class="trend-label">周五</span>
                </div>
                <div class="trend-item">
                  <div class="trend-bar" style="height: 20%;"></div>
                  <span class="trend-label">周六</span>
                </div>
                <div class="trend-item">
                  <div class="trend-bar" style="height: 15%;"></div>
                  <span class="trend-label">周日</span>
                </div>
              </div>
            </div>
          </div>
          
          <div class="chart-card">
            <h3 class="chart-title">任务优先级分布</h3>
            <div class="chart-content">
              <div class="priority-chart">
                <div class="priority-item">
                  <div class="priority-bar high" style="width: 35%;"></div>
                  <span class="priority-label">高优先级</span>
                  <span class="priority-value">35%</span>
                </div>
                <div class="priority-item">
                  <div class="priority-bar medium" style="width: 45%;"></div>
                  <span class="priority-label">中优先级</span>
                  <span class="priority-value">45%</span>
                </div>
                <div class="priority-item">
                  <div class="priority-bar low" style="width: 20%;"></div>
                  <span class="priority-label">低优先级</span>
                  <span class="priority-value">20%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="charts-section">
          <div class="chart-card full-width">
            <h3 class="chart-title">任务完成率</h3>
            <div class="chart-content">
              <div class="completion-chart">
                <div class="completion-circle">
                  <div class="completion-inner">
                    <span class="completion-percentage">{{ statistics.completionRate }}%</span>
                    <span class="completion-label">完成率</span>
                  </div>
                </div>
                <div class="completion-details">
                  <div class="completion-item">
                    <span class="completion-color completed"></span>
                    <span class="completion-text">已完成: {{ statistics.completedTasks }} 个任务</span>
                  </div>
                  <div class="completion-item">
                    <span class="completion-color pending"></span>
                    <span class="completion-text">待处理: {{ statistics.pendingTasks }} 个任务</span>
                  </div>
                  <div class="completion-item">
                    <span class="completion-color overdue"></span>
                    <span class="completion-text">逾期: {{ statistics.overdueTasks }} 个任务</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      // 用户信息
      user: JSON.parse(localStorage.getItem('user') || '{}'),
      
      // 日期范围
      dateRange: null,
      
      // 统计数据
      statistics: {
        totalTasks: 0,
        completedTasks: 0,
        pendingTasks: 0,
        overdueTasks: 0,
        completionRate: 0,
        totalTasksChange: 0,
        completedTasksChange: 0,
        pendingTasksChange: 0,
        overdueTasksChange: 0
      }
    }
  },
  mounted() {
    // 加载统计数据
    this.loadStatistics();
  },
  methods: {
    // 加载统计数据
    loadStatistics() {
      this.$http.get('/api/tasks/statistics')
        .then(response => {
          if (response.success) {
            const data = response.data;
            this.statistics = {
              totalTasks: data.total,
              completedTasks: data.completed,
              pendingTasks: data.pending,
              overdueTasks: data.overdue,
              completionRate: data.completionRate,
              totalTasksChange: 12, // 模拟数据
              completedTasksChange: 15, // 模拟数据
              pendingTasksChange: -5, // 模拟数据
              overdueTasksChange: -10 // 模拟数据
            };
          }
        })
        .catch(error => {
          console.error('加载统计数据失败:', error);
        });
    },
    
    // 处理日期变更
    handleDateChange(date) {
      this.dateRange = date;
      // 这里可以根据日期范围重新获取统计数据
      this.loadStatistics();
    },
    
    // 处理菜单点击
    handleMenuClick({ key }) {
      if (key === 'task-list') {
        this.$router.push('/taskflow/task-list');
      } else if (key === 'user-management') {
        this.$router.push('/taskflow/user-management');
      }
    },
    
    // 处理退出登录
    handleLogout() {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      this.$router.push('/taskflow/login');
    }
  }
}
</script>

<style lang="less">
.statistics-container {
  min-height: 100vh;
  background-color: #f5f5f5;
  
  .pc-statistics {
    display: flex;
    min-height: 100vh;
    
    .sidebar {
      width: 240px;
      background-color: #fff;
      border-right: 1px solid #e5e5e5;
      display: flex;
      flex-direction: column;
      
      .sidebar-header {
        padding: 24px;
        border-bottom: 1px solid #e5e5e5;
        
        .logo-icon {
          font-size: 32px;
          font-weight: 700;
          color: #4CAF50;
          margin-bottom: 8px;
        }
        
        .logo-text {
          font-size: 18px;
          font-weight: 600;
          color: #1A1A1A;
        }
      }
      
      .sidebar-menu {
        flex: 1;
        padding: 16px 0;
        
        .ant-menu {
          border-right: none;
        }
      }
      
      .sidebar-footer {
        padding: 24px;
        border-top: 1px solid #e5e5e5;
        
        .user-info {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 16px;
          
          .user-name {
            font-size: 14px;
            color: #1A1A1A;
          }
        }
      }
    }
    
    .main-content {
      flex: 1;
      padding: 32px;
      
      .main-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 24px;
        
        .page-title {
          font-size: 24px;
          font-weight: 600;
          color: #1A1A1A;
        }
      }
      
      .stats-overview {
        display: flex;
        gap: 24px;
        margin-bottom: 24px;
        
        .stat-card {
          flex: 1;
          background-color: #fff;
          border-radius: 8px;
          padding: 24px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          display: flex;
          align-items: center;
          gap: 16px;
          
          .stat-icon {
            width: 48px;
            height: 48px;
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 24px;
            
            &.task-icon {
              background-color: #E3F2FD;
              color: #2196F3;
            }
            
            &.completed-icon {
              background-color: #E8F5E9;
              color: #4CAF50;
            }
            
            &.pending-icon {
              background-color: #FFF3E0;
              color: #FF9800;
            }
            
            &.overdue-icon {
              background-color: #FFEBEE;
              color: #F44336;
            }
          }
          
          .stat-content {
            flex: 1;
            
            .stat-title {
              font-size: 14px;
              color: #666666;
              margin-bottom: 4px;
            }
            
            .stat-value {
              font-size: 24px;
              font-weight: 600;
              color: #1A1A1A;
              margin-bottom: 4px;
            }
            
            .stat-change {
              font-size: 12px;
              
              &.positive {
                color: #4CAF50;
              }
              
              &.negative {
                color: #F44336;
              }
            }
          }
        }
      }
      
      .charts-section {
        display: flex;
        gap: 24px;
        margin-bottom: 24px;
        
        .chart-card {
          flex: 1;
          background-color: #fff;
          border-radius: 8px;
          padding: 24px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          
          &.full-width {
            flex: 100%;
          }
          
          .chart-title {
            font-size: 16px;
            font-weight: 500;
            color: #1A1A1A;
            margin-bottom: 16px;
          }
          
          .chart-content {
            // 图表内容
          }
        }
      }
      
      .trend-chart {
        display: flex;
        align-items: flex-end;
        gap: 16px;
        height: 200px;
        padding: 16px 0;
        
        .trend-item {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          
          .trend-bar {
            width: 100%;
            background-color: #4CAF50;
            border-radius: 4px 4px 0 0;
            transition: height 0.3s ease;
          }
          
          .trend-label {
            font-size: 12px;
            color: #666666;
          }
        }
      }
      
      .priority-chart {
        display: flex;
        flex-direction: column;
        gap: 12px;
        
        .priority-item {
          display: flex;
          align-items: center;
          gap: 12px;
          
          .priority-bar {
            flex: 1;
            height: 12px;
            border-radius: 6px;
            
            &.high {
              background-color: #F44336;
            }
            
            &.medium {
              background-color: #FF9800;
            }
            
            &.low {
              background-color: #4CAF50;
            }
          }
          
          .priority-label {
            font-size: 14px;
            color: #666666;
            width: 80px;
          }
          
          .priority-value {
            font-size: 14px;
            font-weight: 500;
            color: #1A1A1A;
            width: 40px;
            text-align: right;
          }
        }
      }
      
      .completion-chart {
        display: flex;
        align-items: center;
        gap: 48px;
        
        .completion-circle {
          width: 200px;
          height: 200px;
          border-radius: 50%;
          background: conic-gradient(#4CAF50 0% 70%, #E0E0E0 70% 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          
          .completion-inner {
            width: 160px;
            height: 160px;
            background-color: #fff;
            border-radius: 50%;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            
            .completion-percentage {
              font-size: 32px;
              font-weight: 600;
              color: #4CAF50;
            }
            
            .completion-label {
              font-size: 14px;
              color: #666666;
              margin-top: 4px;
            }
          }
        }
        
        .completion-details {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 16px;
          
          .completion-item {
            display: flex;
            align-items: center;
            gap: 12px;
            
            .completion-color {
              width: 16px;
              height: 16px;
              border-radius: 50%;
              
              &.completed {
                background-color: #4CAF50;
              }
              
              &.pending {
                background-color: #FF9800;
              }
              
              &.overdue {
                background-color: #F44336;
              }
            }
            
            .completion-text {
              font-size: 14px;
              color: #666666;
            }
          }
        }
      }
    }
  }
}
</style>